import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach stored session token to every request as Authorization header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('ws_token') || sessionStorage.getItem('ws_token');
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  setPassword: (newPassword) => api.post('/auth/set-password', { new_password: newPassword }),
  updateProfile: (data) => api.put('/auth/profile', data),
  getFirebaseToken: () => api.get('/auth/firebase-token'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/auth/profile/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const adminAPI = {
  resetAccount: (memberId) => api.post(`/admin/reset-account/${memberId}`),
  approveAccount: (memberId) => api.post(`/admin/approve-account/${memberId}`),
};

export const servicesAPI = {
  getAll: (params = {}) => api.get('/services', { params }),
  getOne: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  updateTeamAssignments: (id, teamAssignments) => api.put(`/services/${id}/team-assignments`, { team_assignments: teamAssignments }),
  delete: (id) => api.delete(`/services/${id}`),
  clone: (id) => api.post(`/services/${id}/clone`),
  notifyChange: (id, changeType) => api.post(`/services/${id}/notify-change`, { change_type: changeType }),
  uploadAttachment: (serviceId, itemId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/services/${serviceId}/items/${itemId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAttachment: (serviceId, itemId, attachmentId) =>
    api.delete(`/services/${serviceId}/items/${itemId}/attachments/${attachmentId}`),
  uploadSetlistAttachment: (serviceId, songId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/services/${serviceId}/setlist/${songId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteSetlistAttachment: (serviceId, songId, attachmentId) =>
    api.delete(`/services/${serviceId}/setlist/${songId}/attachments/${attachmentId}`),
};

export const teamAPI = {
  getAll: (params = {}) => api.get('/team', { params }),
  create: (data) => api.post('/team', data),
  update: (id, data) => api.put(`/team/${id}`, data),
  delete: (id) => api.delete(`/team/${id}`),
};

export const notificationsAPI = {
  getAll: (afterTs = null, limit = 1) => {
    const params = new URLSearchParams({ limit });
    if (afterTs) params.set('after_ts', afterTs);
    return api.get(`/notifications?${params}`);
  },
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
  clearAll: (readOnly = false) => api.delete(`/notifications${readOnly ? '?read_only=true' : ''}`),
  testPush: () => api.post('/notifications/test-push'),
};

export const serviceChatAPI = {
  getMessages: (serviceId, skip = 0, limit = 30) =>
    api.get(`/services/${serviceId}/chat/messages?skip=${skip}&limit=${limit}`),
  sendMessage: (serviceId, text, replyToId = null) =>
    api.post(`/services/${serviceId}/chat/messages`, { text, ...(replyToId ? { reply_to_id: replyToId } : {}) }),
  // last_count: the msg_count stored when user last opened Discussion
  getUnread: (serviceId) =>
    api.get(`/services/${serviceId}/chat/unread`),
  getUnreadBulk: (services) =>
    api.post('/services/chat/unread-bulk', { services }),
  markRead: (serviceId) =>
    api.post(`/services/${serviceId}/chat/mark-read`),
  setUserState: (serviceId, data) =>
    api.post(`/services/${serviceId}/user-state`, data),
  react: (serviceId, msgId, emoji) =>
    api.post(`/services/${serviceId}/chat/messages/${msgId}/react`, { emoji }),
  deleteMessage: (serviceId, msgId) =>
    api.delete(`/services/${serviceId}/chat/messages/${msgId}`),
};

export const userAPI = {
  resolve: (userIds) => api.post('/users/resolve', { user_ids: userIds }),
};

export const chatAPI = {
  getChats: () => api.get('/chats'),
  startChat: (recipientUserId) => api.post('/chats', { recipient_user_id: recipientUserId }),
  startChatByEmail: (email) => api.post('/chats', { recipient_email: email }),
  getMessages: (chatId, skip = 0, limit = 30) => api.get(`/chats/${chatId}/messages?skip=${skip}&limit=${limit}`),
  sendMessage: (chatId, text, replyToId = null) =>
    api.post(`/chats/${chatId}/messages`, { text, ...(replyToId ? { reply_to_id: replyToId } : {}) }),
  markRead: (chatId) => api.post(`/chats/${chatId}/mark-read`),
  setTyping: (chatId, typing) => api.post(`/chats/${chatId}/typing`, { typing }),
  react: (chatId, msgId, emoji) => api.post(`/chats/${chatId}/messages/${msgId}/react`, { emoji }),
  deleteMessage: (chatId, msgId) => api.delete(`/chats/${chatId}/messages/${msgId}`),
};

export const songsAPI = {
  getAll: ({ cursor = null, limit = 30, search = '', language = '', sort_by = 'title' } = {}) => {
    const params = new URLSearchParams({ limit, sort_by });
    if (cursor) params.set('cursor', cursor);
    if (search) params.set('search', search);
    if (language) params.set('language', language);
    return api.get(`/songs?${params.toString()}`);
  },
  getAllForPicker: () => api.get('/songs/picker'),
  /** Batch metadata fetch for specific song IDs (max 50) — used by Lineup on mount */
  getPickerByIds: (ids) => api.get('/songs/picker', { params: { ids: ids.join(',') } }),
  /** Server-side paginated search for the Lineup song picker dialog */
  searchPicker: ({ q = '', lang = '', cursor = '', limit = 20 } = {}) =>
    api.get('/songs/picker', { params: { q, lang, cursor, limit } }),
  getById: (id) => api.get(`/songs/${id}`),
  create: (data) => api.post('/songs', data),
  update: (id, data) => api.put(`/songs/${id}`, data),
  delete: (id) => api.delete(`/songs/${id}`),
  updateContent: (id, content) => api.put(`/songs/${id}/content`, { content }),
  getRecentServices: (id) => api.get(`/songs/${id}/services`),
  syncDropbox: () => api.post('/songs/sync-dropbox'),
  resetStuckSync: () => api.delete('/songs/sync-dropbox/reset'),
  getSyncStatus: (jobId) => api.get(`/songs/sync-dropbox/${jobId}`),
  getSyncGlobalStatus: () => api.get('/songs/sync-dropbox/status'),
};

export const pushAPI = {
  getVapidKey: () => api.get('/push/vapid-key'),
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  send: (data) => api.post('/push/send', data),
};

export const webAuthnAPI = {
  check: (email) => api.get(`/auth/webauthn/check?email=${encodeURIComponent(email)}`),
  registerBegin: () => api.post('/auth/webauthn/register/begin'),
  registerComplete: (challenge_id, credential, device_name) =>
    api.post('/auth/webauthn/register/complete', { challenge_id, credential, device_name }),
  loginBegin: (email) => api.post('/auth/webauthn/login/begin', { email }),
  loginComplete: (challenge_id, credential) =>
    api.post('/auth/webauthn/login/complete', { challenge_id, credential }),
  listCredentials: () => api.get('/auth/webauthn/credentials'),
  deleteCredential: (cred_id) => api.delete(`/auth/webauthn/credentials/${cred_id}`),
};

export const rolesAPI = {
  getAll: () => api.get('/roles'),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  reorder: (role_ids) => api.put('/roles/reorder', { role_ids }),
};

export const groupsAPI = {
  getAll: () => api.get('/groups'),
  create: (data) => api.post('/groups', data),
  update: (id, data) => api.put(`/groups/${id}`, data),
  delete: (id) => api.delete(`/groups/${id}`),
};

export const serviceTypesAPI = {
  getAll: () => api.get('/service-types'),
  create: (data) => api.post('/service-types', data),
  update: (id, data) => api.put(`/service-types/${id}`, data),
  delete: (id) => api.delete(`/service-types/${id}`),
};

export const lineupsAPI = {
  getAll: () => api.get('/lineups'),
  create: (name) => api.post('/lineups', { name }),
  getById: (id) => api.get(`/lineups/${id}`),
  update: (id, name, items) => api.put(`/lineups/${id}`, { name, items }),
  delete: (id) => api.delete(`/lineups/${id}`),
  share: (id, userIds) => api.post(`/lineups/${id}/share`, { user_ids: userIds }),
  notify: (id) => api.post(`/lineups/${id}/notify`),
  promote: (id, serviceId) => api.post(`/lineups/${id}/promote`, { service_id: serviceId }),
  getEligibleServices: ({ q = '', cursor = '', limit = 20 } = {}) =>
    api.get('/lineups/eligible-services', { params: { q, cursor, limit } }),
  getShareableUsers: ({ q = '', cursor = '', limit = 30 } = {}) =>
    api.get('/lineups/shareable-users', { params: { q, cursor, limit } }),
};

export const availabilityAPI = {
  getMine: () => api.get('/availability'),
  add: (startDate, endDate, reason) => api.post('/availability', { start_date: startDate, end_date: endDate || startDate, reason }),
  remove: (blockId) => api.delete(`/availability/${blockId}`),
  getTeam: () => api.get('/availability/team'),
};